import React, { memo, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ReactAudioPlayer from "react-audio-player";
import { makeStyles } from "@material-ui/core/styles";
import { Grid, Chip, CircularProgress } from "../../../components";
import api, { baseURL } from "../../../api/api";

const voiceBasePath = `${baseURL}/api/v1/voice_service`;

const useStyles = makeStyles({
  preview: {
    maxWidth: "100%",
  },
  previewItem: {
    maxWidth: "100%",
    minWidth: 0,
  },
  wrapChip: {
    height: "auto",
    maxWidth: "100%",
    "& .MuiChip-label": {
      display: "block",
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
  },
});

export const createFilePath = (path) => {
  const finalPath = `${voiceBasePath}/assets?path=${encodeURIComponent(path)}`;
  return finalPath;
};

const audioStyle = { width: "100%", maxWidth: "100%", minWidth: 200 };

/** Approver links use JWT only; <audio src> cannot send Authorization, so we fetch with axios and play a blob. */
function VoiceStepAudio({ path }) {
  const token = useSelector((s) => s.params?.token);
  const [blobSrc, setBlobSrc] = useState(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!token) {
      return undefined;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await api.get("/api/v1/voice_service/assets", {
          params: { path },
          responseType: "blob",
          hideLoader: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) {
          return;
        }
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        const url = URL.createObjectURL(data);
        blobUrlRef.current = url;
        setBlobSrc(url);
      } catch (e) {
        if (!cancelled) {
          setBlobSrc("");
        }
      }
    };

    setBlobSrc(null);
    load();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [path, token]);

  if (!token) {
    return (
      <ReactAudioPlayer
        src={createFilePath(path)}
        controls
        preload="metadata"
        style={audioStyle}
      />
    );
  }

  if (blobSrc === null) {
    return <CircularProgress size={28} />;
  }
  if (blobSrc === "") {
    return null;
  }

  return (
    <ReactAudioPlayer
      src={blobSrc}
      controls
      preload="metadata"
      style={audioStyle}
    />
  );
}

const renderData = (
  { stepType, variableName, path, step, transcript },
  classes
) => {
  if (stepType === "customerData" && variableName) {
    return <Chip label={variableName} className={classes.wrapChip} />;
  } else if (stepType === "dtmf") {
    return <Chip label="dtmf" />;
  } else if (stepType === "ttsData") {
    return <Chip label={transcript} className={classes.wrapChip} />;
  } else if (stepType === "goto") {
    return <Chip label={`Direction (${step})`} className={classes.wrapChip} />;
  } else {
    return <VoiceStepAudio path={path} />;
  }
};

function VoicePreview({ voiceArray = [] }) {
  const classes = useStyles();

  return (
    <Grid
      container
      spacing={2}
      alignItems="center"
      wrap="wrap"
      className={classes.preview}
    >
      {voiceArray.map((data) => (
        <Grid
          item
          key={data._id}
          className={classes.previewItem}
          xs={data.stepType === "ttsData" ? 12 : undefined}
        >
          {renderData(data, classes)}
        </Grid>
      ))}
    </Grid>
  );
}

export default memo(VoicePreview);
